import { apiClient } from '@tradermind/shared/api';

// Web Push 通知服务
class WebPushNotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string | null = null;

  // 检查浏览器是否支持推送通知
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // 获取当前通知权限状态
  getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  // 请求通知权限
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn('[WebPush] Push notifications not supported');
      return 'denied';
    }
    
    const permission = await Notification.requestPermission();
    console.log('[WebPush] Permission status:', permission);
    return permission;
  }

  // 注册 Service Worker
  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!this.isSupported()) {
      console.warn('[WebPush] Service Worker not supported');
      return null;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('[WebPush] Service Worker registered:', this.swRegistration);
      return this.swRegistration;
    } catch (error) {
      console.error('[WebPush] Service Worker registration failed:', error);
      return null;
    }
  }

  // 从后端获取 VAPID 公钥
  async getVapidPublicKey(): Promise<string | null> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await apiClient.get('/push/vapid-key');
      console.log('[WebPush] VAPID API response:', response.data);
      // apiClient 已自动解包，响应格式: { vapid_public_key: "..." }
      this.vapidPublicKey = response.data?.vapid_public_key || null;
      console.log('[WebPush] Parsed VAPID public key:', this.vapidPublicKey ? 'found' : 'not found');
      return this.vapidPublicKey;
    } catch (error) {
      console.error('[WebPush] Failed to get VAPID public key:', error);
      return null;
    }
  }

  // 订阅推送通知
  async subscribe(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      console.warn('[WebPush] Push notifications not supported');
      return null;
    }

    // 确保有权限
    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      console.warn('[WebPush] Notification permission denied');
      return null;
    }

    // 确保 Service Worker 已注册
    if (!this.swRegistration) {
      this.swRegistration = await this.registerServiceWorker();
    }
    if (!this.swRegistration) {
      console.error('[WebPush] No Service Worker registration');
      return null;
    }

    // 获取 VAPID 公钥
    const vapidKey = await this.getVapidPublicKey();
    if (!vapidKey) {
      console.error('[WebPush] No VAPID public key');
      return null;
    }

    try {
      // 检查是否已有订阅
      let subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (!subscription) {
        // 创建新订阅
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
        });
        console.log('[WebPush] New subscription created:', subscription);
      } else {
        console.log('[WebPush] Existing subscription found:', subscription);
      }

      // 将订阅信息发送到后端
      await this.registerSubscriptionToBackend(subscription);
      
      return subscription;
    } catch (error) {
      console.error('[WebPush] Subscription failed:', error);
      return null;
    }
  }

  // 取消订阅
  async unsubscribe(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        // 从后端移除订阅
        await this.removeSubscriptionFromBackend(subscription);
        // 取消浏览器订阅
        await subscription.unsubscribe();
        console.log('[WebPush] Unsubscribed successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[WebPush] Unsubscribe failed:', error);
      return false;
    }
  }

  // 将订阅信息注册到后端
  private async registerSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionJson = subscription.toJSON();
    
    try {
      await apiClient.post('/devices/register', {
        token: JSON.stringify(subscriptionJson),
        platform: 'web',
        device_name: this.getBrowserName()
      });
      console.log('[WebPush] Subscription registered to backend');
    } catch (error) {
      console.error('[WebPush] Failed to register subscription to backend:', error);
      throw error;
    }
  }

  // 从后端移除订阅
  private async removeSubscriptionFromBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionJson = subscription.toJSON();
    
    try {
      await apiClient.delete('/devices/remove', {
        data: {
          token: JSON.stringify(subscriptionJson)
        }
      });
      console.log('[WebPush] Subscription removed from backend');
    } catch (error) {
      console.error('[WebPush] Failed to remove subscription from backend:', error);
    }
  }

  // 获取浏览器名称
  private getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  }

  // 将 Base64 URL 编码转换为 Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray as Uint8Array<ArrayBuffer>;
  }

  // 初始化推送通知（用户登录后调用）
  async initialize(): Promise<boolean> {
    if (!this.isSupported()) {
      console.log('[WebPush] Push notifications not supported in this browser');
      return false;
    }

    // 注册 Service Worker
    const registration = await this.registerServiceWorker();
    if (!registration) {
      return false;
    }

    // 如果已有权限，自动订阅
    if (this.getPermissionStatus() === 'granted') {
      await this.subscribe();
      return true;
    }

    return true;
  }
}

export const webPushService = new WebPushNotificationService();
export default webPushService;
